import { Component, ErrorInfo, ReactNode } from 'react'
import { Button } from '@/components/ui'
import { WarningIcon } from '@/icons'
import { reportError } from '@/lib/errorReporter'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // `reportError` already logs to the console in dev and forwards to the
    // backend in prod, so a separate `console.error` call here was just
    // noise (and surfaced twice in the devtools for every error).
    reportError(error, {
      source: 'ErrorBoundary',
      componentStack: errorInfo.componentStack,
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-base flex items-center justify-center p-4">
          <div className="bg-surface rounded-lg p-8 max-w-md w-full text-center">
            <div className="text-red-500 mb-4">
              <WarningIcon className="w-16 h-16 mx-auto" />
            </div>
            <h1 className="text-xl font-bold text-primary mb-2">Something went wrong</h1>
            <p className="text-muted mb-6">An unexpected error occurred. Try reloading the page.</p>
            {this.state.error && (
              <details className="text-left mb-6">
                <summary className="text-muted cursor-pointer text-sm">Error details</summary>
                <pre className="text-xs text-red-400 bg-base p-3 rounded mt-2 overflow-auto max-h-32">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex gap-3 justify-center">
              <Button variant="secondary" onClick={this.handleReset}>
                Try Again
              </Button>
              <Button onClick={this.handleReload}>Reload Page</Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
